/**
 * Viewfinder — Cadre de visée caméra
 *
 * Quatre coins en SVG, centrés à l'écran.
 * Aide les utilisateurs à vision résiduelle à cadrer l'œuvre.
 * Entièrement décoratif pour les utilisateurs sans vision (aria-hidden).
 *
 * Le composant s'anime légèrement à l'activation du scanning :
 *   repos     → coins blancs, opacity 0.7
 *   scanning  → coins cuivrés, légère pulsation
 *
 * Props :
 *   isScanning {boolean} — change la couleur pendant la reconnaissance
 */

import React from 'react'
import { motion, useReducedMotion } from 'framer-motion'

const COPPER    = '#B08D5B'
const OFF_WHITE = '#F8F4EC'

const Viewfinder = ({ isScanning = false }) => {
  const color = isScanning ? COPPER : OFF_WHITE
  const prefersReduced = useReducedMotion()

  // Taille du cadre et longueur des coins
  const size    = 200
  const corner  = 24    // longueur du trait de coin
  const stroke  = 2     // épaisseur

  return (
    <motion.div
      aria-hidden="true"
      style={{
        position:      'absolute',
        left:          '50%',
        top:           '44%',
        transform:     'translate(-50%, -50%)',
        width:         `${size}px`,
        height:        `${size}px`,
        pointerEvents: 'none',
      }}
      animate={{ opacity: isScanning && !prefersReduced ? [0.7, 1, 0.7] : 0.7 }}
      transition={isScanning && !prefersReduced
        ? { duration: 1.2, repeat: Infinity, ease: 'easeInOut' }
        : { duration: 0.3 }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* ── Coin haut-gauche ─────────────────────────────── */}
        <line x1={0}       y1={corner} x2={0}       y2={0}      stroke={color} strokeWidth={stroke} strokeLinecap="round"/>
        <line x1={0}       y1={0}      x2={corner}  y2={0}      stroke={color} strokeWidth={stroke} strokeLinecap="round"/>

        {/* ── Coin haut-droite ─────────────────────────────── */}
        <line x1={size}    y1={corner} x2={size}    y2={0}      stroke={color} strokeWidth={stroke} strokeLinecap="round"/>
        <line x1={size}    y1={0}      x2={size - corner} y2={0} stroke={color} strokeWidth={stroke} strokeLinecap="round"/>

        {/* ── Coin bas-gauche ──────────────────────────────── */}
        <line x1={0}       y1={size - corner} x2={0}    y2={size}      stroke={color} strokeWidth={stroke} strokeLinecap="round"/>
        <line x1={0}       y1={size}           x2={corner} y2={size}   stroke={color} strokeWidth={stroke} strokeLinecap="round"/>

        {/* ── Coin bas-droite ──────────────────────────────── */}
        <line x1={size}    y1={size - corner} x2={size}        y2={size} stroke={color} strokeWidth={stroke} strokeLinecap="round"/>
        <line x1={size}    y1={size}           x2={size - corner} y2={size} stroke={color} strokeWidth={stroke} strokeLinecap="round"/>

        {/* ── Croix centrale (guide de mise au point) ──────── */}
        <line
          x1={size / 2 - 8} y1={size / 2}
          x2={size / 2 + 8} y2={size / 2}
          stroke={color} strokeWidth={1} strokeLinecap="round" opacity={0.4}
        />
        <line
          x1={size / 2} y1={size / 2 - 8}
          x2={size / 2} y2={size / 2 + 8}
          stroke={color} strokeWidth={1} strokeLinecap="round" opacity={0.4}
        />
      </svg>
    </motion.div>
  )
}

export default Viewfinder
